import React, { Component } from "react";
import { withModulesManager, withHistory, formatMessage } from "@openimis/fe-core";
import { injectIntl } from "react-intl";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  Typography,
  Paper,
  Box,
  Grid,
  IconButton,
  Card,
  CardContent,
  CircularProgress,
} from "@material-ui/core";
import { ArrowBack as BackIcon, Edit as EditIcon } from "@material-ui/icons";
import { withStyles } from "@material-ui/core/styles";
import {
  fetchPolicyholderEnrollment,
  submitPolicyholderEnrollment,
  reviewPolicyholderEnrollment,
  approvePolicyholderEnrollment,
  rejectPolicyholderEnrollment,
  turnToDraftPolicyholderEnrollment,
  deletePolicyholderEnrollment,
} from "../../actions";
import EnrollmentStatusBadge from "../../components/common/EnrollmentStatusBadge";
import EnrollmentActionButtons from "../../components/common/EnrollmentActionButtons";
import KeyValueSection from "../../components/common/KeyValueSection";
import {
  policyholderFieldRows,
  enrollmentMetaRows,
  parseJsonField,
  formatDisplayValue,
  openCreatedEnrollmentEntity,
} from "../../utils/enrollmentData";

const styles = (theme) => ({
  page: { margin: theme.spacing(2) },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2),
  },
  titleWrap: { display: "flex", alignItems: "center", gap: theme.spacing(2), flex: 1 },
  title: { fontWeight: 700, color: theme.palette.primary.main },
  card: { marginBottom: theme.spacing(2) },
});

class PolicyHolderEnrollmentDetail extends Component {
  componentDidMount() {
    this.load();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.match?.params?.enrollmentId !==
      this.props.match?.params?.enrollmentId
    ) {
      this.load();
    }
  }

  load = () => {
    const uuid = this.props.match?.params?.enrollmentId;
    if (uuid) {
      this.props.fetchPolicyholderEnrollment(this.props.modulesManager, uuid);
    }
  };

  handleBack = () => {
    const { history } = this.props;
    if (history) history.push("/enrollment/policyholder");
    else window.location.href = "/enrollment/policyholder";
  };

  handleEdit = () => {
    const uuid = this.props.enrollment?.uuid;
    if (!uuid) return;
    const { history } = this.props;
    if (history) history.push(`/enrollment/policyholder/${uuid}/edit`);
    else window.location.href = `/enrollment/policyholder/${uuid}/edit`;
  };

  handleOpenCreated = () => {
    const { modulesManager, history, enrollment } = this.props;
    openCreatedEnrollmentEntity(
      modulesManager,
      history,
      "policyHolder",
      enrollment
    );
  };

  run = (action, payload) =>
    Promise.resolve(action(this.props.modulesManager, payload)).then(() =>
      this.load()
    );

  render() {
    const { classes, intl, enrollment } = this.props;
    if (!enrollment) {
      return (
        <Box className={classes.page} display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      );
    }

    const phRows = policyholderFieldRows(enrollment.policyholderData);
    const metaRows = enrollmentMetaRows(enrollment);
    const contact = parseJsonField(enrollment.contactInfo);
    const contactRows = [
      { label: "Email", value: formatDisplayValue(contact.email) },
      { label: "Phone", value: formatDisplayValue(contact.phone) },
    ];
    const created = enrollment.createdPolicyholder;

    return (
      <Box className={classes.page}>
        <Box className={classes.header}>
          <Box className={classes.titleWrap}>
            <IconButton onClick={this.handleBack}>
              <BackIcon />
            </IconButton>
            <Typography variant="h5" className={classes.title}>
              {formatMessage(intl, "enrollment", "policyHolderEnrollment.detail") ||
                "Policyholder Enrollment"}
            </Typography>
            <EnrollmentStatusBadge status={enrollment.status} />
          </Box>
          {enrollment.status === "DRAFT" && (
            <IconButton color="primary" onClick={this.handleEdit}>
              <EditIcon />
            </IconButton>
          )}
        </Box>

        <Card className={classes.card}>
          <CardContent>
            <EnrollmentActionButtons
              intl={intl}
              enrollment={enrollment}
              onEdit={this.handleEdit}
              onOpenCreated={
                enrollment.createdPolicyholder?.id != null
                  ? this.handleOpenCreated
                  : undefined
              }
              openCreatedLabel="Open created policy holder"
              onSubmit={(p) => this.run(this.props.submitPolicyholderEnrollment, p)}
              onReview={(p) => this.run(this.props.reviewPolicyholderEnrollment, p)}
              onApprove={(p) =>
                this.run(this.props.approvePolicyholderEnrollment, p)
              }
              onReject={(p) => this.run(this.props.rejectPolicyholderEnrollment, p)}
              onTurnToDraft={(p) =>
                this.run(this.props.turnToDraftPolicyholderEnrollment, p)
              }
              onDelete={(uuid) =>
                this.run(this.props.deletePolicyholderEnrollment, uuid).then(
                  this.handleBack
                )
              }
            />
          </CardContent>
        </Card>

        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <Paper className={classes.card}>
              <Box p={2}>
                <KeyValueSection title="Policyholder details" rows={phRows} />
                <KeyValueSection title="Contact" rows={contactRows} />
                {created && (
                  <KeyValueSection
                    title="Created policyholder"
                    rows={[
                      { label: "Code", value: created.code || "—" },
                      {
                        label: "Trade name",
                        value: created.tradeName || "—",
                      },
                      { label: "ID", value: String(created.id || "—") },
                    ]}
                  />
                )}
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={5}>
            <Paper className={classes.card}>
              <Box p={2}>
                <KeyValueSection title="Enrollment info" rows={metaRows} />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  }
}

const mapStateToProps = (state) => ({
  enrollment: state.enrollment?.policyHolderEnrollmentDetail,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchPolicyholderEnrollment,
      submitPolicyholderEnrollment,
      reviewPolicyholderEnrollment,
      approvePolicyholderEnrollment,
      rejectPolicyholderEnrollment,
      turnToDraftPolicyholderEnrollment,
      deletePolicyholderEnrollment,
    },
    dispatch
  );

export default withModulesManager(
  withHistory(
    injectIntl(
      withStyles(styles)(
        connect(
          mapStateToProps,
          mapDispatchToProps
        )(PolicyHolderEnrollmentDetail)
      )
    )
  )
);
